import reactStringReplace from 'react-string-replace';
import React from 'react';
import { WlWindow, MyNavigator } from '../typings/globals/cordova';
import { BehaviorSubject, Observable } from 'rxjs';

const availableLanguages = require('resources/languages.json');

declare const window: WlWindow;
declare const navigator: MyNavigator;

function loadLocale(lang: string) {
  switch (lang) {
    case 'fr':
      return import('date-fns/locale/fr');
    case 'de':
      return import('date-fns/locale/de');
    case 'nl':
      return import('date-fns/locale/nl');
    case 'es':
      return import('date-fns/locale/es');
    case 'it':
      return import('date-fns/locale/it');
    default:
      return import('date-fns/locale/en-IN');
  }
}

interface TranslatorState {
  lang: string;
  translations?: {
    [key: string]: string;
  };
  loading?: boolean;
}

function cleanLang(preferredLanguage) {
  let preferredLanguageStripped = preferredLanguage.substring(0, 2);
  if (availableLanguages.indexOf(preferredLanguageStripped) === -1) {
    preferredLanguageStripped = 'en';
  }
  return preferredLanguageStripped;
}

export const getLangFromUrl = (): string => {
  const pathname = window.location.pathname;

  const [lang] = pathname.split('/')
    .filter(el => availableLanguages.indexOf(el) > -1);

  if (!lang && window.isPreRender) {
    return 'en';
  }

  return lang;
};

const getLangFromStorage = (): string => {
  try {
    return window.localStorage &&
    window.localStorage.getItem('langCode') &&
    window.localStorage.getItem('langCode') !== 'undefined'
      ? window.localStorage.getItem('langCode').toLowerCase()
      : null;
  } catch (e) {
    return null;
  }
};

const getLangFromBrowser = (): string => {
  return navigator.language || navigator.userLanguage;
};

const getPreferredLanguage = (): string => {
  const langFromUrl = getLangFromUrl();
  const langFromStorage = getLangFromStorage();
  const langFromBrowser = getLangFromBrowser();
  return cleanLang(langFromUrl || langFromStorage || langFromBrowser || 'en');
};

export class Translator {
  // static translations = null;
  private state$: BehaviorSubject<TranslatorState>;
  private dateLocale$ = new BehaviorSubject(null);
  private static singleton: Translator = new Translator({
    lang: getPreferredLanguage(),
    loading: true,
    translations: {},
  });

  private constructor(state: TranslatorState) {
    this.state$ = new BehaviorSubject(state);
    this.state$.subscribe(state => console.log(`[Translator] state `, { lang: state.lang, loading: state.loading }));
  }

  static get(): Observable<Translator> {
    if (!Translator.singleton) {
      const preferredLanguage = getPreferredLanguage();
      console.log(`[Translator] preferredLanguage ${preferredLanguage}`);
      Translator.singleton = new Translator({ lang: cleanLang(preferredLanguage), loading: true, translations: {} });
    }
    return Translator.singleton.state$
      .switchMap(({ lang, loading }) => loading ? Translator.singleton.getTranslations(lang) : Promise.resolve(Translator.singleton))
      .mapTo(Translator.singleton)
      .filter(t => !t.state$.value.loading)
      .shareReplay(1);
  }

  static readonly language$ = Translator.get()
    .switchMap(t => t.state$)
    .map(({ lang }) => lang)
    .distinctUntilChanged()
    .shareReplay(1);

  static setLanguageCode(preferredLanguage: string) {
    const lang = cleanLang(preferredLanguage);
    try {
      window.localStorage.setItem('langCode', lang);
    } catch(e) {}
    Translator.singleton.state$.next({ lang });
    return Translator.singleton.getTranslations(lang);
  }

  static getLanguageCode() {
    return Translator.singleton.lang();
  }

  static getLanguageSource() {
    const currentLang = Translator.singleton.lang();
    const langFromUrl = getLangFromUrl();
    const langFromStorage = getLangFromStorage();
    const langFromBrowser = getLangFromBrowser();
    if (currentLang === langFromUrl) {
      return 'url';
    }
    if (currentLang === langFromStorage) {
      return 'storage';
    }
    if (currentLang === langFromBrowser) {
      return 'browser';
    }
    return 'default';
  }

  lang(): string {
    return this.state$.value.lang;
  }

  translations() {
    return this.state$.value.translations;
  }

  static getDateLocale() {
    return Translator.singleton.dateLocale$.getValue();
  }

  getTranslations(lang: string): Promise<Translator> {
    if (process.env.NODE_ENV === 'test') {
      this.setTranslations(lang, {});
      return Promise.resolve(this);
    }
    console.log('[Translator] updateTranslations', lang);
    const langPromiseFactory = () => {
      return import(`resources/${lang}.i18n.json`)
        .then((translations) => {
          if (typeof translations === 'function') {
            return translations();
          }
          return translations;
        });
    };
    return loadLocale(lang)
      .then(locale => this.dateLocale$.next(locale))
      .then(() => langPromiseFactory())
      .then((translations) => {
        this.setTranslations(lang, translations);
        console.log('[Translator] updateTranslations has written translations');
        return this;
      });
  }

  private setTranslations(lang: string, translations: { [key: string]: string }) {
    return this.state$.next({ lang, translations, loading: false });
  }

  private handleMissingTranslation(translateKey) {
    const msg = `[Translator] Could not translate key: ${translateKey}`;
    if (window.debugTranslations) {
      if (window.location.hostname !== 'www.withlocals.com' ) {
        console.warn(msg);
      } else {
        console.trace(msg);
      }
    }
  }

  static translateText(translateKey: string, replacement: any = {}, defaultValue = ''): string {
    return this.singleton.translate(translateKey, replacement, true, defaultValue) as string;
  }

  // TODO: convert to return JSX element only, remove teh textOnly attribute and then we have more type safety
  static translate(translateKey: string, replacement: any = {}, textOnly?: boolean, defaultValue?: string | JSX.Element): any {
    return this.singleton.translate(translateKey, replacement, textOnly, defaultValue) as JSX.Element;
  }

  static i18n(key: string, defaultText?: string): string {
    return this.translate(key, {}, true, defaultText);
  }

  translate(translateKey: string, replacement: any = {}, textOnly?: boolean, defaultValue?: string | JSX.Element): JSX.Element | string {
    let text: any = this.translations()?.[translateKey] || '';
    const lang = this.lang();

    if (!text) {
      this.handleMissingTranslation(translateKey);
      if (!!defaultValue) {
        text = defaultValue;
      }
    }

    let replacementIndex = 0;
    for (const key in replacement) {
      if (!replacement.hasOwnProperty(key)) {
        continue;
      }
      // tslint:disable-next-line:no-increment-decrement
      replacementIndex++;
      const replacementKey = `{${key}}`;
      // eslint-disable-next-line no-loop-func
      text = reactStringReplace(text, replacementKey, (match, i) => {
        const replaceResult = replacement[key];
        if (
          typeof replaceResult === 'object' &&
          React.isValidElement(replaceResult)
        ) {
          return React.cloneElement<any>(replaceResult, {
            key: `${replacementIndex}-${i}-${lang}-${translateKey}`,
          });
        }
        return replaceResult;
      });
    }

    if (textOnly) {
      if (text instanceof Array) {
        return text.join('');
      }
      return text;
    }

    return <span key={`${lang}-${translateKey}`} data-translatekey={translateKey}>{text}</span>;
  }
}
